import React, { useState } from 'react';

const isOtherValue = (v) => ['other', 'others'].includes((v || '').toLowerCase());

// Select with "Other" text-input support.
//
// Bug we just fixed: previously, picking "Other" called onChange('') which made
// `showInput` evaluate to false on the next render — the input would never
// appear. We now track "in other mode" with a local state flag (`otherActive`)
// that's independent of the stored value, so the input shows immediately and
// keeps showing as the user types.
function SelectWithOther({ field, value, onChange, error }) {
  const selectClass = `form-select${error ? ' is-invalid' : ''}`;
  const opts = field.options || [];
  const hasOther = opts.some(o => isOtherValue(o.value));
  const knownOpt = opts.find(o => o.value === value);
  // If the persisted value isn't in the option list, the user previously typed
  // a custom "other" — auto-enter "other mode" so the input shows pre-filled.
  const isCustomStored = hasOther && !knownOpt && !!value;
  const [otherActive, setOtherActive] = useState(isCustomStored);

  const showInput = hasOther && (otherActive || isCustomStored);
  const otherOptValue = opts.find(o => isOtherValue(o.value))?.value || 'other';
  const selectVal = otherActive || isCustomStored ? otherOptValue : (value || '');

  const handleSelect = (v) => {
    if (isOtherValue(v)) {
      // Mark as "other mode" but keep value blank until the user types.
      setOtherActive(true);
      onChange('');
    } else {
      setOtherActive(false);
      onChange(v);
    }
  };
  const handleOtherText = (t) => onChange(t);

  return (
    <div>
      <select className={selectClass} value={selectVal} onChange={e => handleSelect(e.target.value)}>
        <option value="">-- Select {field.label} --</option>
        {opts.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>
      {showInput && (
        <input type="text" className="form-control mt-2"
          placeholder={`Please specify the ${field.label.toLowerCase()}…`}
          value={value || ''}
          onChange={e => handleOtherText(e.target.value)}
          autoFocus />
      )}
    </div>
  );
}

// Radio with "Other" text-input support
function RadioWithOther({ field, value, onChange }) {
  const opts = field.options || [];
  const hasOther = opts.some(o => isOtherValue(o.value));
  const knownOpt = opts.find(o => o.value === value);
  const isCustom = hasOther && !knownOpt && value !== '' && value !== undefined;
  const [otherText, setOtherText] = useState(isCustom ? value : '');
  const showInput = hasOther && (isOtherValue(value) || isCustom);

  const handleChange = (v) => {
    if (isOtherValue(v)) { setOtherText(''); onChange(''); }
    else { onChange(v); }
  };
  const radioVal = isCustom ? (opts.find(o => isOtherValue(o.value))?.value || 'other') : (value || '');

  return (
    <div>
      <div className="d-flex flex-wrap gap-3 mt-1">
        {opts.map(opt => (
          <div className="form-check" key={opt.value}>
            <input className="form-check-input" type="radio" id={`${field.fieldKey}_${opt.value}`}
              value={opt.value} checked={radioVal === opt.value} onChange={() => handleChange(opt.value)} />
            <label className="form-check-label" htmlFor={`${field.fieldKey}_${opt.value}`}>{opt.label}</label>
          </div>
        ))}
      </div>
      {showInput && (
        <input type="text" className="form-control mt-2" placeholder="Please specify…"
          style={{ maxWidth: 300 }}
          value={otherText} onChange={e => { setOtherText(e.target.value); onChange(e.target.value); }}
          autoFocus={!isCustom} />
      )}
    </div>
  );
}

function FieldInput({ field, value, onChange, error }) {
  const baseClass = `form-control${error ? ' is-invalid' : ''}`;

  switch (field.fieldType) {
    case 'textarea':
      return <textarea className={baseClass} value={value || ''} placeholder={field.placeholder} rows={3} onChange={e => onChange(e.target.value)} />;

    case 'select':
      return <SelectWithOther field={field} value={value} onChange={onChange} error={error} />;

    case 'radio':
      return <RadioWithOther field={field} value={value} onChange={onChange} />;

    case 'checkbox':
      return (
        <div className="form-check">
          <input className="form-check-input" type="checkbox" id={field.fieldKey}
            checked={!!value} onChange={e => onChange(e.target.checked)} />
          <label className="form-check-label" htmlFor={field.fieldKey}>{field.label}</label>
        </div>
      );

    case 'boolean':
      return (
        <div className="form-check form-switch">
          <input className="form-check-input" type="checkbox" role="switch" id={field.fieldKey}
            checked={!!value} onChange={e => onChange(e.target.checked)} />
          <label className="form-check-label" htmlFor={field.fieldKey}>{value ? 'Yes' : 'No'}</label>
        </div>
      );

    case 'date':
      return <input type="date" className={baseClass} value={value || ''} onChange={e => onChange(e.target.value)} />;

    case 'datetime':
      return <input type="datetime-local" className={baseClass} value={value || ''} onChange={e => onChange(e.target.value)} />;

    case 'number':
      return <input type="number" className={baseClass} value={value || ''} placeholder={field.placeholder} onChange={e => onChange(e.target.value)} />;

    case 'currency':
      return (
        <div className="input-group">
          <span className="input-group-text">₹</span>
          <input type="number" className={baseClass} value={value || ''} placeholder="0.00" step="0.01" onChange={e => onChange(e.target.value)} />
        </div>
      );

    case 'percentage':
      return (
        <div className="input-group">
          <input type="number" className={baseClass} value={value || ''} placeholder="0" min="0" max="100" onChange={e => onChange(e.target.value)} />
          <span className="input-group-text">%</span>
        </div>
      );

    case 'email':
      return <input type="email" className={baseClass} value={value || ''} placeholder={field.placeholder || 'email@example.com'} onChange={e => onChange(e.target.value)} />;

    case 'phone':
      return <input type="tel" className={baseClass} value={value || ''} placeholder={field.placeholder || '+91 9876543210'} onChange={e => onChange(e.target.value)} />;

    case 'url':
      return <input type="url" className={baseClass} value={value || ''} placeholder={field.placeholder || 'https://'} onChange={e => onChange(e.target.value)} />;

    case 'color':
      return (
        <div className="d-flex align-items-center gap-2">
          <input type="color" className="form-control form-control-color" value={value || '#000000'} onChange={e => onChange(e.target.value)} style={{ width: 50 }} />
          <input type="text" className={baseClass} value={value || ''} placeholder="#000000" onChange={e => onChange(e.target.value)} />
        </div>
      );

    case 'image':
    case 'file':
      return <input type="file" className={`form-control${error ? ' is-invalid' : ''}`} accept={field.fieldType === 'image' ? 'image/*' : undefined} onChange={e => onChange(e.target.files[0])} />;

    default: // text
      return <input type="text" className={baseClass} value={value || ''} placeholder={field.placeholder} onChange={e => onChange(e.target.value)} />;
  }
}

export default function DynamicForm({ fields = [], initialData = {}, onSubmit, onCancel, loading, submitLabel = 'Save' }) {
  const [formData, setFormData] = useState(() => {
    const init = {};
    fields.forEach(f => {
      init[f.fieldKey] = initialData[f.fieldKey] ?? f.defaultValue ?? '';
    });
    return init;
  });
  const [errors, setErrors] = useState({});

  const formFields = fields.filter(f => f.showInForm !== false && f.isActive !== false);

  // Group fields by group name
  const groups = {};
  formFields.forEach(f => {
    const g = f.group || 'General';
    if (!groups[g]) groups[g] = [];
    groups[g].push(f);
  });

  const validate = () => {
    const errs = {};
    formFields.forEach(f => {
      const val = formData[f.fieldKey];
      if (f.isRequired && (val === undefined || val === null || val === '')) {
        errs[f.fieldKey] = `${f.label} is required`;
      }
      if (val && f.validationRules) {
        const rules = f.validationRules;
        if (rules.minLen && String(val).length < rules.minLen) errs[f.fieldKey] = `Min ${rules.minLen} characters`;
        if (rules.maxLen && String(val).length > rules.maxLen) errs[f.fieldKey] = `Max ${rules.maxLen} characters`;
        if (rules.min && Number(val) < rules.min) errs[f.fieldKey] = `Min value: ${rules.min}`;
        if (rules.max && Number(val) > rules.max) errs[f.fieldKey] = `Max value: ${rules.max}`;
        if (rules.pattern && !new RegExp(rules.pattern).test(val)) errs[f.fieldKey] = rules.message || 'Invalid format';
      }
    });
    return errs;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    onSubmit(formData);
  };

  const setField = (key, val) => {
    setFormData(prev => ({ ...prev, [key]: val }));
    if (errors[key]) setErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      {Object.entries(groups).map(([groupName, gFields]) => (
        <div className="form-group-section" key={groupName}>
          <div className="form-group-section-title">{groupName}</div>
          <div className="row g-3">
            {gFields.sort((a, b) => (a.order || 0) - (b.order || 0)).map(field => (
              <div className={field.width || 'col-md-6'} key={field.fieldKey}>
                {field.fieldType !== 'checkbox' && field.fieldType !== 'boolean' && (
                  <label className="form-label">
                    {field.label}
                    {field.isRequired && <span className="text-danger ms-1">*</span>}
                  </label>
                )}
                <FieldInput
                  field={field}
                  value={formData[field.fieldKey]}
                  onChange={(val) => setField(field.fieldKey, val)}
                  error={errors[field.fieldKey]}
                />
                {errors[field.fieldKey] && (
                  <div className="invalid-feedback d-block" style={{ fontSize: 12 }}>
                    {errors[field.fieldKey]}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="d-flex gap-2 justify-content-end mt-3">
        {onCancel && <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel} disabled={loading}>Cancel</button>}
        <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
          {loading && <span className="spinner-border spinner-border-sm me-1"></span>}
          {submitLabel}
        </button>
      </div>
    </form>
  );
}
